import type {
  ComponentType,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { IconAlert, IconCheck, IconChevronDown } from '../../../shared/console'
import { FIELD_BASE, FIELD_WITH_ICON, LABEL } from './styles'

/**
 * The public tier's form controls, drawn to Figma P1/P3/P4: a 13px medium label
 * with an asterisk on required answers, a 36px field, an optional hint under it,
 * and an error that replaces the hint — icon + colour + text, never colour
 * alone, and wired to the field with `aria-describedby`.
 */

type Icon = ComponentType<{ size?: number; className?: string }>

export function Field({
  id,
  label,
  required = false,
  hint,
  error,
  children,
  className,
}: {
  id: string
  label: ReactNode
  required?: boolean
  hint?: ReactNode
  error?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <label htmlFor={id} className={LABEL}>
        {label}
        {required ? (
          <span aria-hidden="true" className="text-ink">
            {' '}
            *
          </span>
        ) : null}
      </label>
      <div className="mt-2">{children}</div>
      {error ? (
        <p id={`${id}-error`} className="mt-1.5 flex items-start gap-1.5 font-dash text-[12px] leading-4 text-danger">
          <IconAlert size={13} className="mt-px shrink-0" />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1.5 font-dash text-[12px] leading-4 text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export function TextInput({
  icon: LeadIcon,
  invalid = false,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { icon?: Icon; invalid?: boolean }) {
  return (
    <div className="relative">
      {LeadIcon ? (
        <LeadIcon size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-soft" />
      ) : null}
      <input
        aria-invalid={invalid || undefined}
        className={`${LeadIcon ? FIELD_WITH_ICON : FIELD_BASE} ${className ?? ''}`}
        {...rest}
      />
    </div>
  )
}

export function SelectInput({
  placeholder,
  options,
  invalid = false,
  className,
  value,
  ...rest
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> & {
  placeholder: string
  options: readonly string[]
  invalid?: boolean
}) {
  const empty = value === '' || value === undefined
  return (
    <div className="relative">
      <select
        aria-invalid={invalid || undefined}
        value={value}
        className={`${FIELD_BASE} cursor-pointer appearance-none pr-9 ${empty ? 'text-muted' : 'text-ink'} ${className ?? ''}`}
        {...rest}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((o) => (
          <option key={o} value={o} className="text-ink">
            {o}
          </option>
        ))}
      </select>
      <IconChevronDown
        size={16}
        className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-ink"
      />
    </div>
  )
}

export function TextArea({
  invalid = false,
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      aria-invalid={invalid || undefined}
      className={`${FIELD_BASE} h-24 resize-y py-2.5 ${className ?? ''}`}
      {...rest}
    />
  )
}

/** Figma's 18px ink checkbox: a real checkbox, drawn over. */
export function Checkbox({
  id,
  checked,
  onChange,
  label,
  invalid = false,
  describedById,
}: {
  id: string
  checked: boolean
  onChange: (checked: boolean) => void
  label: ReactNode
  invalid?: boolean
  describedById?: string
}) {
  return (
    <label htmlFor={id} className="inline-flex cursor-pointer items-start gap-2.5 font-dash text-[13px] leading-5 text-ink">
      <span className="relative mt-px inline-flex h-[18px] w-[18px] shrink-0">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          aria-invalid={invalid || undefined}
          aria-describedby={describedById}
          onChange={(e) => {
            onChange(e.target.checked)
          }}
          className={`peer h-[18px] w-[18px] cursor-pointer appearance-none rounded-[5px] border bg-surface transition-colors checked:border-primary checked:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 ${
            invalid ? 'border-danger' : 'border-line-strong'
          }`}
        />
        <IconCheck
          size={12}
          strokeWidth={3}
          className="pointer-events-none absolute top-[3px] left-[3px] text-primary-ink opacity-0 peer-checked:opacity-100"
        />
      </span>
      <span>{label}</span>
    </label>
  )
}

/** A status line under a form: icon + colour + text, announced to screen readers. */
export function FormAlert({
  tone = 'danger',
  children,
  testId,
}: {
  tone?: 'danger' | 'warning'
  children: ReactNode
  testId?: string
}) {
  const palette =
    tone === 'danger'
      ? 'border-pill-danger-line bg-pill-danger-bg text-pill-danger-fg'
      : 'border-pill-warning-line bg-pill-warning-bg text-pill-warning-fg'
  return (
    <div
      role="alert"
      data-testid={testId}
      className={`flex items-start gap-2 rounded-md border px-3 py-2.5 font-dash text-[12.5px] leading-[18px] ${palette}`}
    >
      <IconAlert size={15} className="mt-px shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  )
}
